import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  LoadingSm,
} from "@medimo/ui";
import { useMemo } from "react";
import {
  type SignInFormSchemaType,
  type SignInOutput,
  useSignInForm,
} from "../../hooks/use-sign-in-form";
import { PasswordInput } from "../password-input";

const JAPANESE_CHARACTER_PATTERN = /[ぁ-んァ-ヶ一-龠]/;

type SignInFormProps = {
  isPending: boolean;
  defaultValues: SignInFormSchemaType;
  errorMessage?: string | null;
  onSignInSubmit?: VoidFunction;
  onSuccessToSignIn?: VoidFunction;
  onRequireNewPassword?: (result: SignInOutput) => void;
  onRequireTwoFactor?: VoidFunction;
};

export const SignInForm = ({
  isPending,
  defaultValues,
  errorMessage,
  onSignInSubmit,
  onSuccessToSignIn,
  onRequireNewPassword,
  onRequireTwoFactor,
}: SignInFormProps) => {
  const { form, handleSubmit, isLoading } = useSignInForm({
    defaultValues,
    onSuccessToSignIn,
    onRequireNewPassword,
    onRequireTwoFactor,
    onMutation: onSignInSubmit,
  });

  const formErrorMessage = useMemo(() => {
    const message = form.formState.errors.root?.message;
    if (!message) {
      return message;
    }
    // アカウントの存在有無を推測させないため、パスワード誤りと未登録は同一の文言にする
    if (
      message === "Incorrect username or password." ||
      message === "User does not exist."
    ) {
      return "メールアドレスまたはパスワードが間違っています。";
    }
    // Cognito はパスワード誤りと同じ NotAuthorizedException で返すため、メッセージ文字列で判別する
    if (message.includes("Temporary password has expired")) {
      return "仮パスワードの有効期限が切れています。「パスワードを忘れた方はこちら」からパスワードを再発行してください。";
    }
    // 連続失敗によるロックアウトは再発行しても解除されず、時間経過でのみ解除される
    if (message.includes("Password attempts exceeded")) {
      return "ログインの試行回数が上限に達しました。しばらく時間をおいてから再度お試しください。";
    }
    // アプリ側で組み立てた日本語メッセージはそのまま表示する
    if (JAPANESE_CHARACTER_PATTERN.test(message)) {
      return message;
    }
    // 未知のメッセージ（Cognito の文言変更等）は英語のまま表示しない。原因を特定できないため
    // 再発行で解決すると断定せず、時間をおいた再試行と問い合わせ先もあわせて案内する
    return "ログインできませんでした。しばらく時間をおいてから再度お試しください。解決しない場合は「パスワードを忘れた方はこちら」からパスワードを再発行するか、サポートへお問い合わせください。";
  }, [form.formState.errors.root?.message]);

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-8">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>メールアドレス</FormLabel>
              <FormControl>
                <Input
                  placeholder="example@medimo.ai"
                  autoComplete="email"
                  {...field}
                  onBlur={() => {
                    field.onChange(field.value.trim());
                    field.onBlur();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>パスワード</FormLabel>
              <FormControl>
                <PasswordInput
                  type="password"
                  autoComplete="current-password"
                  {...field}
                  onBlur={() => {
                    field.onChange(field.value.trim());
                    field.onBlur();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormMessage>{formErrorMessage}</FormMessage>
        <FormMessage>{errorMessage}</FormMessage>
        <Button
          type="submit"
          className="flex w-full gap-3"
          disabled={isPending || isLoading}
        >
          {isPending || isLoading ? (
            <LoadingSm title="ログイン中" />
          ) : (
            "ログイン"
          )}
        </Button>
      </form>
    </Form>
  );
};
